import { useCallback, useState } from "react";
import { resolveRange, type Period } from "./dateRange";

// v3 B-5: Dashboard / Campaigns / Analytics の3画面で共有する期間状態。
// localStorageに永続化し、画面を切り替えても選択中の期間が保たれる（同じPeriodSwitcherを表示する前提）。
const STORAGE_KEY = "stoq-ad-console:period:v1";
const DEFAULT_PERIOD: Period = "7d";
const PERIOD_VALUES: Period[] = ["7d", "30d", "90d", "custom"];

interface StoredPeriod {
  period: Period;
  customStart: string;
  customEnd: string;
}

function defaultCustomRange(): { start: string; end: string } {
  // カスタム未選択時のinput[type=date]初期値として、直近30日を計算しておく（空文字のままだと壊れるため）。
  const { from, to } = resolveRange("30d", { start: "", end: "" });
  return { start: from, end: to };
}

function loadStoredPeriod(): StoredPeriod {
  const fallback = defaultCustomRange();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { period: DEFAULT_PERIOD, customStart: fallback.start, customEnd: fallback.end };
    const parsed = JSON.parse(raw);
    const period: Period = PERIOD_VALUES.includes(parsed?.period) ? parsed.period : DEFAULT_PERIOD;
    const customStart = typeof parsed?.customStart === "string" && parsed.customStart ? parsed.customStart : fallback.start;
    const customEnd = typeof parsed?.customEnd === "string" && parsed.customEnd ? parsed.customEnd : fallback.end;
    return { period, customStart, customEnd };
  } catch {
    return { period: DEFAULT_PERIOD, customStart: fallback.start, customEnd: fallback.end };
  }
}

function saveStoredPeriod(v: StoredPeriod): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // localStorageが使えない環境（プライベートモード等）では黙って諦める。UI動作は継続。
  }
}

export interface UsePeriodResult {
  period: Period;
  customStart: string;
  customEnd: string;
  /** 現在の選択から計算済みのfrom/to（'YYYY-MM-DD'）。API呼び出しにそのまま渡す。 */
  range: { from: string; to: string };
  setPeriod: (p: Period) => void;
  setCustomStart: (v: string) => void;
  setCustomEnd: (v: string) => void;
}

/** Dashboard / Campaigns / Analytics で共有する期間フック（localStorage永続化・既定7d）。 */
export function usePeriod(): UsePeriodResult {
  const [state, setState] = useState<StoredPeriod>(() => loadStoredPeriod());

  // 関数型更新（setState(prev => ...)）にすることで、setPeriod/setCustomStart/setCustomEndは
  // stateに依存せず参照が安定する（依存配列は空のまま・呼び出し元での古いstateキャプチャも防ぐ）。
  const setPeriod = useCallback((p: Period) => {
    setState((prev) => {
      const next = { ...prev, period: p };
      saveStoredPeriod(next);
      return next;
    });
  }, []);

  const setCustomStart = useCallback((v: string) => {
    setState((prev) => {
      const next = { ...prev, customStart: v };
      saveStoredPeriod(next);
      return next;
    });
  }, []);

  const setCustomEnd = useCallback((v: string) => {
    setState((prev) => {
      const next = { ...prev, customEnd: v };
      saveStoredPeriod(next);
      return next;
    });
  }, []);

  const range = resolveRange(state.period, { start: state.customStart, end: state.customEnd });

  return {
    period: state.period,
    customStart: state.customStart,
    customEnd: state.customEnd,
    range,
    setPeriod,
    setCustomStart,
    setCustomEnd,
  };
}
